import { useEffect, useRef, useState } from "react";
import { ArrowRight, BookOpen, Stethoscope } from "lucide-react";
import { Sparkle, StarBurst, Squiggle } from "./Decorations";

const HEAD_WORDS = ["Run.", "Jump.", "Leap."];

export default function Hero() {
  const [y, setY] = useState(0);
  const imgRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onMouseMove = (e) => {
    const el = imgRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (py - 0.5) * -8;
    const ry = (px - 0.5) * 10;
    el.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(${Math.min(y * 0.08, 60)}px)`;
  };
  const onMouseLeave = () => {
    if (imgRef.current)
      imgRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(${Math.min(y * 0.08, 60)}px)`;
  };

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-32 pb-24 overflow-hidden bg-gradient-to-b from-white via-lavender-bg/60 to-white"
    >
      <span
        className="float-shape animate-float"
        style={{ width: 280, height: 280, background: "#C7A5D9", top: -40, left: -60 }}
      />
      <span
        className="float-shape animate-float"
        style={{ width: 220, height: 220, background: "#DDEBF0", bottom: 40, right: -40, animationDelay: "2s" }}
      />
      <span
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 right-[-260px] w-[600px] h-[600px] rounded-full opacity-30 pointer-events-none"
        style={{
          background:
            "conic-gradient(from 30deg, #C7A5D9, transparent 30%, #DDEBF0 60%, transparent 90%, #C7A5D9)",
          filter: "blur(40px)",
          animation: "spin 28s linear infinite",
        }}
      />

      {/* Fun floating decorations */}
      <Sparkle className="absolute top-32 right-[42%]" color="#8E44AD" size={22} />
      <Sparkle className="absolute bottom-32 left-[44%]" color="#C7A5D9" size={14} />
      <StarBurst className="absolute top-40 right-10 opacity-40 hidden lg:block" size={70} />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative z-10">
          <div className="hero-glow" aria-hidden />
          <div className="relative">
            <div className="animate-fade-in">
              <p className="pill mb-6" data-testid="hero-pill">
                Dr. Maggie Cadet . Board-Certified Rheumatologist . NYC
              </p>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display uppercase tracking-[0.16em] text-plum text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
            >
              {HEAD_WORDS.map((w, wi) => (
                <span key={w} className="block overflow-hidden">
                  <span
                    className="inline-block animate-slideUp"
                    style={{ animationDelay: `${wi * 0.15}s`, color: wi === 1 ? "#8E44AD" : undefined }}
                  >
                    {w.split("").map((ch, i) => (
                      <span
                        key={i}
                        className="inline-block"
                        style={{
                          animation: `letterFloat 6s ease-in-out infinite`,
                          animationDelay: `${wi * 0.4 + i * 0.08}s`,
                        }}
                      >
                        {ch === " " ? "\u00A0" : ch}
                      </span>
                    ))}
                  </span>
                </span>
              ))}
              <span className="block overflow-hidden mt-2">
                <span
                  className="inline-block animate-slideUp font-display uppercase tracking-[0.24em] text-2xl sm:text-3xl text-royal"
                  style={{ animationDelay: "0.6s" }}
                >
                  With Dr. Maggie Cadet.
                </span>
              </span>
            </h1>

            <div className="mt-3 max-w-[280px]">
              <Squiggle className="w-full h-4 opacity-70" color="#8E44AD" />
            </div>

            <p
              data-testid="hero-subheadline"
              className="mt-7 text-lg sm:text-xl text-charcoal/85 max-w-xl animate-fade-up"
              style={{ animationDelay: "0.7s" }}
            >
              Taking care of the immune system and our joints is important for good health.
            </p>

            <p
              className="mt-4 text-[15px] leading-relaxed text-charcoal/70 max-w-xl animate-fade-up"
              style={{ animationDelay: "0.85s" }}
            >
              Dr. Maggie Cadet raises awareness about autoimmune diseases, arthritis, joint pain, lupus,
              osteoporosis, women's health, minority health, fitness, and rheumatologic conditions
              that disproportionately affect women and individuals in minority communities.
            </p>

            <div
              className="mt-9 flex flex-wrap gap-3 animate-fade-up"
              style={{ animationDelay: "1s" }}
            >
              <MagneticButton testid="hero-cta-joint" onClick={() => go("joint-health")} primary>
                <Stethoscope size={16} /> Learn About Joint Health
              </MagneticButton>
              <MagneticButton testid="hero-cta-blog" onClick={() => go("blog")}>
                <BookOpen size={16} /> Read the Blog
              </MagneticButton>
              <MagneticButton testid="hero-cta-contact" onClick={() => go("contact")}>
                Connect with Dr. Maggie Cadet <ArrowRight size={16} />
              </MagneticButton>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div
            ref={imgRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className="relative mx-auto max-w-md lg:max-w-none transition-transform duration-300 ease-out will-change-transform"
            style={{ transform: `translateY(${Math.min(y * 0.08, 60)}px)` }}
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-lavender via-pale-blue to-white opacity-70 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-lavender/60 shadow-[0_30px_80px_-30px_rgba(75,31,99,0.55)] bg-gradient-to-br from-lavender-bg via-white to-pale-blue/60 h-[620px] flex items-end justify-center">
              <img
                src="/images/hero-portrait.webp?v=5"
                alt="Dr. Maggie Cadet in athletic wear smiling with hands on hips, advocating for movement, strength, and joint health"
                className="w-auto max-h-[520px] object-contain object-bottom"
                loading="eager"
                fetchPriority="high"
                width="760"
                height="1065"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-plum/15 via-transparent to-transparent pointer-events-none" />
            </div>

            <div
              data-testid="hero-floating-badge"
              className="hidden sm:block absolute -bottom-6 -left-6 animate-fade-up"
              style={{ animationDelay: "1.1s" }}
            >
              <img
                src="/images/logo-seal-sm.webp"
                alt="Dr. Maggie Cadet, MD, Rheumatologist seal"
                width="112"
                height="112"
                loading="lazy"
                decoding="async"
                className="h-28 w-28 object-contain hover:scale-110 hover:rotate-6 transition-transform duration-700 drop-shadow-[0_12px_24px_rgba(75,31,99,0.45)]"
              />
            </div>

            {/* Decorative orbiting sparkles */}
            <Sparkle className="absolute -top-4 -right-4" color="#8E44AD" size={26} />
            <Sparkle className="absolute top-1/2 -right-10 hidden lg:block" color="#C7A5D9" size={20} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(110%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.9s cubic-bezier(.2,.7,.2,1) both; }
        @keyframes letterFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes spin {
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </section>
  );
}

function MagneticButton({ children, onClick, testid, primary = false }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = e.clientX - r.left - r.width / 2;
    const my = e.clientY - r.top - r.height / 2;
    el.style.transform = `translate(${mx * 0.18}px, ${my * 0.25}px)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "translate(0,0)";
  };
  return (
    <button
      ref={ref}
      data-testid={testid}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`${primary ? "btn-purple" : "btn-outline-purple"} transition-transform duration-300 ease-out`}
    >
      {children}
    </button>
  );
}
